import { useState } from 'react';
import { auditsAPI, type AuditDetail } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { AlertTriangle, CheckCircle2, FileDown, Loader2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { saveBlob } from '../../utils/fileDownload';
import { apiErrorAsync } from '../audits/useAuditActions';
import { AUDIT_OUTCOME } from '../audits/auditStatus';

interface AuditCompletedBannerProps {
  audit: AuditDetail;
  canReopen: boolean;
  /** Resolves null on success, or the reason it failed (shown in the dialog). */
  onReopen: (reason: string) => Promise<string | null>;
}

/**
 * Shown on a completed (or expired) audit: when, outcome, validity, findings
 * and open actions. Approvers can reopen it with a reason.
 */
export default function AuditCompletedBanner({ audit, canReopen, onReopen }: AuditCompletedBannerProps) {
  const { formatDate } = useFormatters();
  const [open, setOpen] = useState<boolean>(false);
  const [reason, setReason] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState<boolean>(false);

  /** The report the customer sends to the supplier. */
  const exportPdf = async () => {
    setExporting(true);
    try {
      const { blob, filename } = await auditsAPI.exportPdf(audit.id);
      saveBlob(blob, filename, 'application/pdf');
    } catch (err: unknown) {
      toast.error(await apiErrorAsync(err, 'Could not export the audit report.'));
    } finally {
      setExporting(false);
    }
  };
  const expired = audit.status === 'expired';
  const outcome = audit.outcome ? AUDIT_OUTCOME[audit.outcome] : null;

  const submit = async () => {
    if (!reason.trim()) return;
    setSaving(true);
    setError(null);
    const failure = await onReopen(reason.trim());
    setSaving(false);
    if (failure) setError(failure);
    else { setOpen(false); setReason(''); }
  };

  return (
    <div className={`rounded-lg border p-4 ${expired
      ? 'border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40'
      : 'border-green-300 bg-green-50 dark:border-green-800 dark:bg-green-950/40'}`} role="status">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2 font-medium">
            {expired ? <AlertTriangle className="h-4 w-4 text-amber-600" /> : <CheckCircle2 className="h-4 w-4 text-green-600" />}
            <span>
              Completed {audit.completed_at ? formatDate(audit.completed_at) : ''}
              {audit.completed_by_name ? ` by ${audit.completed_by_name}` : ''}
            </span>
            {outcome && <Badge className={`${outcome.className} border-0`}>{outcome.label}</Badge>}
          </div>
          <p className="text-sm text-muted-foreground">
            {audit.valid_until
              ? `${expired ? 'Expired' : 'Valid until'} ${formatDate(audit.valid_until)}`
              : 'Does not expire'}
            {' · '}{audit.findings_count} {audit.findings_count === 1 ? 'finding' : 'findings'}
            {' · '}{audit.open_actions_count} open {audit.open_actions_count === 1 ? 'action' : 'actions'}
          </p>
          {audit.summary && <p className="text-sm whitespace-pre-wrap pt-1">{audit.summary}</p>}
          {!audit.archived && (
            <p className="text-xs text-muted-foreground">The answers are read-only. Follow-up notes and actions stay open.</p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => void exportPdf()} disabled={exporting}>
            {exporting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileDown className="h-4 w-4 mr-1.5" />}
            Export PDF
          </Button>
          {canReopen && (
            <Button variant="outline" onClick={() => { setError(null); setOpen(true); }}>
              <RotateCcw className="h-4 w-4 mr-1.5" />Reopen
            </Button>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reopen audit</DialogTitle>
            <DialogDescription>
              The audit goes back to In progress and its validity is cleared until it is completed again.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reopen-reason">Reason *</Label>
            <Textarea id="reopen-reason" rows={3} className="text-base" value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Why the audit needs changing" />
          </div>
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
          <DialogFooter>
            <Button size="lg" variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button size="lg" onClick={submit} disabled={!reason.trim() || saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Reopen audit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
